import { ModalLauncher } from '@accedo/modal';
import { createBrowserHistory } from 'history';
import { CustomHistory, Location } from 'preact-router';
import { logger } from '@accedo/logger';
import { deviceApi } from '@accedo/ctv-device-api';
import { ExitModalRef } from '##/components/ExitPopup/ExitPopup';
import { nav } from '##/init';
import { config, focusCache } from '##/services/store/store';
import { EXIT_MODAL_NAV_ID, isHomeRoute } from '##/utils';
import TealiumBridge from '##/services/analytics/Tealium/TealiumBridge';
import { setLoadingIndicatorVisibility } from '../components/LoadingIndicator/LoadingIndicator';
import GenericStack from '../GenericStack';
import { Messenger } from './Messenger';

class PageNavigationClass {
  subPageRoute$ = new Messenger<string>('');
  appLoaded$ = new Messenger<void>(undefined);
  favouritesButtonPressed$ = new Messenger<boolean>(false);
  triggeredInnovidAds$ = new Messenger<boolean>(false);
  favouriteMoreShowsPressed$ = new Messenger<void>(undefined);

  private history = createBrowserHistory();
  private isBackNavigation = false;
  private prevPathname = window.location.pathname;
  private singlePageHistoryTracker = new GenericStack<string>();

  // Track state of history separate from history object
  // Setting state on the history object isn't allowed on
  // back navigation or on no navigation, so we set our
  // own state alongside the history object. Tried to keep
  // the same history API
  private historyState: Record<string, unknown> = {};

  constructor() {
    this.addRouteChangeListener(this.onRouteChange);
    this.singlePageHistoryTracker.push(this.prevPathname);
  }

  navigateTo = <T = unknown>(route: string, state?: T): void => {
    this.historyState[route.split('/')[1]] = state;
    this.history.push(route);
  };

  replaceRoute = <T = unknown>(route: string, state?: T): void => {
    this.singlePageHistoryTracker.pop();
    this.historyState[route.split('/')[1]] = state;
    this.history.replace(route);
  };

  getCurrentState = <T = unknown>(): T =>
    this.historyState[this.history.location.pathname.split('/')[1]] as T;

  setCurrentState = <T = unknown>(state: T, route?: string): void => {
    const routeKey =
      route?.split('/')[1] || this.history.location.pathname.split('/')[1];
    this.historyState[routeKey] = state;
  };

  addRouteChangeListener = (
    listener: ({ location: { pathname } }: { location: Location }) => void,
  ): (() => void) => this.history.listen(listener);

  showExitPopup = (): void => {
    if (ModalLauncher.isModalOpen()) {
      ModalLauncher.close();

      return;
    }

    this.openExitPopup();
  };

  /**
   * Global back key handling.
   */
  handleGlobalBackKey = (): void => {
    if (ModalLauncher.isModalOpen()) {
      ModalLauncher.close();
    } else if (window.location.pathname === '/') {
      this.openExitPopup();
    } else if (this.singlePageHistoryTracker.length > 1) {
      // go back to the last page that has a different base than the current
      this.isBackNavigation = true;
      const currentPageBase = window.location.pathname.split('/')[1];
      let numPagesBack = 1;
      while (this.singlePageHistoryTracker.length) {
        // We pop from tracker on every loop so always get the 2nd from top page
        const pastPath = this.getNthLastPath(2);
        const pastBase = pastPath.split('/')[1];

        // either we get to a different page or the home page
        if (pastBase !== currentPageBase || !pastBase) {
          if (this.triggeredInnovidAds$.getCurrentValue()) {
            this.triggeredInnovidAds$.resetToDefault();

            logger.info(
              '[PageNavigation] replace route on back when innovidAds were active',
            );

            this.replaceRoute(pastBase ? pastPath : '/');

            return;
          }

          this.history.go(-numPagesBack);
          return;
        }

        // skipping over page in back action so remove it from tracker
        this.singlePageHistoryTracker.pop();
        numPagesBack += 1;
      }
    } else {
      this.clearSinglePageTracker();
      this.history.push('/');
    }
  };

  getHistoryForRouter = (): CustomHistory => {
    // TODO: When preact-router is compatible with history v5,
    // we need to install it and remove this workaround by passing history to Router
    // Open issue on github: https://github.com/preactjs/preact-router/issues/399
    // For now, we will wrap the v5 history
    // so that the wrapper is not different from v4 supported by preact

    const customHistory: Omit<CustomHistory, 'location'> = {
      listen: (callback) =>
        this.history.listen((action) => {
          callback(action.location);
        }),

      push: (...args) => this.history.push(...args),

      replace: (...args) => this.history.replace(...args),
    };
    Object.defineProperty(customHistory, 'location', {
      get: () => this.history.location,
    });
    return customHistory as CustomHistory;
  };

  clearSinglePageTracker = (): void => {
    this.singlePageHistoryTracker = new GenericStack<string>();
    focusCache.tvShowDetails.$clear();
    focusCache.home.$clear();
    focusCache.search.$clear();
    focusCache.tvShowLanding.$clear();
    focusCache.livePage.$clear();
    focusCache.newsPage.$clear();
  };

  getNthLastPath = (howFarBack: number): string =>
    this.singlePageHistoryTracker.copyOfAllItems[
      this.singlePageHistoryTracker.length - howFarBack
    ] || '/';

  private openExitPopup = (): void => {
    ModalLauncher.open({
      component: ExitModalRef,
      props: {
        actions: {
          onExitApp: () => {
            TealiumBridge.onAppExit()
              .then(() => {
                deviceApi.exit?.();
              })
              .catch(() => {
                deviceApi.exit?.();
              });
          },
          onExitModal: () => ModalLauncher.close(),
        },
        defaultFocusId: EXIT_MODAL_NAV_ID,
        navProps: {
          refocusOnUnmount: false,
        },
      },
    });
  };

  private onRouteChange = ({
    location: { pathname },
  }: {
    location: Location;
  }): void => {
    if (pathname !== this.prevPathname) {
      setLoadingIndicatorVisibility?.(true);
      if (this.isBackNavigation) {
        this.singlePageHistoryTracker.pop();
      } else {
        this.singlePageHistoryTracker.push(pathname);
      }

      const appConfig = config.$get().app;
      const geoConfig = config.$get().geo;
      // reload app when initial data is not loaded
      if (this.prevPathname === '/error' && (!appConfig || !geoConfig)) {
        window.location.reload();
      }

      if (isHomeRoute(this.prevPathname ?? '')) {
        focusCache.home.$set({
          lastFocusId: nav.currentFocusNodeId,
        });
      }

      if (
        isHomeRoute(pathname) &&
        this.isBackNavigation &&
        this.singlePageHistoryTracker.length > 0
      ) {
        focusCache.home.$set({
          scrollToPrevious: true,
        });
      }

      this.isBackNavigation = false;
      this.prevPathname = pathname;
    }
  };
}

export const PageNavigation = new PageNavigationClass();
